/**
 * Standard Tool Result Interface
 * 
 * All commands should return results in this standardized format to ensure:
 * - Consistent AI understanding
 * - Reliable error handling
 * - Unified UI presentation
 * - Better debugging and monitoring
 */

export interface StandardToolResult {
  status: 'success' | 'error' | 'partial' | 'requires_approval';
  message: string; // Human-readable description
  data?: any; // Structured data for Claude
  format: 'markdown' | 'plaintext' | 'json' | 'html';
  
  // Enhanced metadata
  metadata?: {
    executionTime?: number;
    affectedEntities?: string[];
    contextChanges?: string[];
    followupSuggestions?: string[];
    method?: string; // Which implementation method was used
  };
  
  // Artifacts system for rich content
  artifacts?: Array<{
    type: 'file' | 'image' | 'document' | 'data' | 'window';
    content: string | object;
    name: string;
    mimeType?: string;
    size?: number;
    id?: string; // For referencing (e.g., windowId)
  }>;
  
  // Enhanced error details
  error?: {
    code: string;
    category: 'validation' | 'execution' | 'timeout' | 'permission' | 'system' | 'cancelled' | 'precondition';
    details?: any;
    suggestions?: string[];
    retryable?: boolean;
  };
  
  // UI hints for better user experience
  ui?: {
    notify?: boolean;
    autoFocus?: string; // windowId to focus
    showInResult?: boolean;
    resultPreview?: 'full' | 'summary' | 'minimal';
    refreshContext?: boolean; // Whether to refresh context after execution
  };
  
  // ✅ NEW: Streaming metadata (indicates tool supports progress updates)
  streaming?: {
    supported: boolean; // Tool can emit progress updates
    eventsEmitted?: string[]; // Types of events: 'search_results', 'analysis_progress', etc.
    source?: 'backend_sse' | 'claude_stream' | 'periodic_poll' | 'handler_callback';
  };
}

export type StandardResultFormat = 'structured' | 'legacy_json_string' | 'raw';

/**
 * Helper function to create success results
 */
export function createSuccessResult(
  message: string,
  data?: any,
  options?: Partial<StandardToolResult>
): StandardToolResult {
  // 🔍 AGGRESSIVE DEBUG: Log what we're creating
  const isDocRead = message?.includes('Read') && (data?.type === 'markdown_content' || data?.type === 'index_range_content');
  
  if (isDocRead) {
    console.log('[createSuccessResult] 🔍 CRITICAL: Creating result for doc_read:', {
      message,
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : [],
      dataType: data?.type,
      dataHasMarkdown: !!data?.markdown,
      dataMarkdownType: typeof data?.markdown,
      dataMarkdownLength: data?.markdown?.length,
      dataMarkdownPreview: data?.markdown?.substring(0, 200)
    });
  }
  
  const result: StandardToolResult = {
    status: 'success',
    message,
    format: 'json',
    data,
    ...options
  };
  
  if (isDocRead) {
    console.log('[createSuccessResult] 🔍 CRITICAL: Result after creation:', {
      status: result.status,
      message: result.message,
      format: result.format,
      hasData: !!result.data,
      dataKeys: result.data ? Object.keys(result.data) : [],
      dataHasMarkdown: !!result.data?.markdown,
      dataMarkdownLength: result.data?.markdown?.length,
      resultKeys: Object.keys(result)
    });
  }
  
  return result;
}

/**
 * Helper function to create error results
 */
export function createErrorResult(
  message: string,
  error: string | Error,
  category: 'validation' | 'execution' | 'timeout' | 'permission' | 'system' | 'cancelled' | 'precondition' = 'execution',
  options?: Partial<StandardToolResult>
): StandardToolResult {
  const errorMessage = error instanceof Error ? error.message : error;
  
  return {
    status: 'error',
    message,
    format: 'plaintext',
    error: {
      code: 'COMMAND_ERROR',
      category,
      details: errorMessage,
      retryable: category !== 'validation' && category !== 'cancelled' // Don't retry cancelled operations
    },
    ...options
  };
}

/**
 * Helper function to create partial results (for progress updates)
 */
export function createPartialResult(
  message: string,
  data?: any,
  progress?: number
): StandardToolResult {
  return {
    status: 'partial',
    message,
    format: 'json',
    data: {
      ...data,
      progress: progress || 0
    }
  };
} 